import { useEffect, useState } from 'react';
import { Check, CloudOff } from 'lucide-react';
import { cn } from '../lib/utils';

// A slim row that tells the coach their game data is saved on the phone but not
// uploaded yet (no signal at the field), then briefly confirms once it syncs.
// `pending` comes from the games listener's hasPendingWrites (App.tsx).
//
// Online, every save is pending for a fraction of a second, so the row only
// appears once a save has waited long enough to mean there is no signal.
const SHOW_AFTER_MS = 3000;
const SYNCED_FOR_MS = 2500;

export function SyncNote({ pending, className }: { pending: boolean; className?: string }) {
  const [state, setState] = useState<'hidden' | 'pending' | 'synced'>('hidden');

  useEffect(() => {
    if (pending) {
      const t = setTimeout(() => setState('pending'), SHOW_AFTER_MS);
      return () => clearTimeout(t);
    }
    setState(prev => (prev === 'pending' ? 'synced' : prev));
  }, [pending]);

  useEffect(() => {
    if (state !== 'synced') return;
    const t = setTimeout(() => setState('hidden'), SYNCED_FOR_MS);
    return () => clearTimeout(t);
  }, [state]);

  if (state === 'hidden') return null;

  return (
    <div
      role="status"
      className={cn(
        'shrink-0 flex items-center justify-center gap-2 px-4 py-2 bg-raised border-b border-hairline print:hidden',
        className,
      )}
    >
      {state === 'pending' ? (
        <>
          <CloudOff className="w-3.5 h-3.5 text-ink-2" />
          <span className="text-[11px] font-semibold text-ink-2">Saved on this phone. Syncs when you get signal.</span>
        </>
      ) : (
        <>
          <Check className="w-3.5 h-3.5 text-accent-ink" />
          <span className="text-[11px] font-semibold text-accent-ink">Back in range. Everything synced.</span>
        </>
      )}
    </div>
  );
}
